import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaPlay } from 'react-icons/fa';
import { handleImageError } from '../utils/imageFallback';
import { useLanguage } from '../i18n';

const Hero = () => {
    const { t } = useLanguage();
    const scrollToSection = (sectionId) => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    };

    const benefits = [t('fast'), t('secure'), t('growthReady')];

    const stats = [
        { number: '50+', label: t('projectsDelivered') },
        { number: '8+', label: t('yearsExperience') },
        { number: '24/7', label: t('supportWhenNeeded') }
    ];

    return (
        <section id="hero" className="relative overflow-hidden bg-paper">
            <div className="container relative mx-auto px-5 pb-20 pt-14 lg:px-8 lg:pb-28 lg:pt-24">
                <div className="grid items-center gap-16 lg:grid-cols-[0.92fr_1.08fr] lg:gap-20">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                        className="relative z-10"
                    >
                        <p className="mb-7 border-l-2 border-primary-600 pl-4 text-xs font-bold uppercase tracking-[0.16em] text-primary-700">{t('heroEyebrow')}</p>

                        <h1 className="max-w-2xl text-5xl font-bold leading-[0.98] text-ink sm:text-6xl lg:text-[5.2rem]">
                            {t('heroTitleLead')}<span className="text-primary-600">{t('heroTitleAccent')}</span>
                        </h1>

                        <p className="mt-7 max-w-xl text-lg leading-8 text-muted sm:text-xl">
                            {t('heroDescription')}
                        </p>

                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                            {benefits.map((text) => (
                                <div key={text} className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <span className="h-1.5 w-1.5 bg-primary-600" /> {text}
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                            <motion.button
                                type="button"
                                onClick={() => scrollToSection('contact')}
                                className="flex min-h-14 items-center justify-center gap-3 rounded-lg bg-primary-600 px-7 text-base font-bold text-white transition-colors hover:bg-primary-700"
                                whileTap={{ scale: 0.97 }}
                            >
                                {t('freeConsultation')} <FaArrowRight className="text-sm" />
                            </motion.button>
                            <motion.button
                                type="button"
                                onClick={() => scrollToSection('case-studies')}
                                className="flex min-h-14 items-center justify-center gap-3 rounded-lg border border-gray-300 bg-transparent px-7 text-base font-bold text-ink transition-colors hover:border-primary-300 hover:text-primary-700"
                                whileTap={{ scale: 0.97 }}
                            >
                                <FaPlay className="text-xs text-primary-600" /> {t('viewProjects')}
                            </motion.button>
                        </div>

                        <div className="mt-12 grid max-w-xl grid-cols-3 gap-4 border-t border-gray-200 pt-6">
                            {stats.map((stat) => (
                                <div key={stat.label}>
                                    <div className="font-display text-2xl font-bold text-ink sm:text-3xl">{stat.number}</div>
                                    <div className="mt-1 text-xs leading-5 text-muted sm:text-sm">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 28 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.18, duration: 0.8, ease: 'easeOut' }}
                        className="relative mx-auto w-full max-w-[650px] lg:mr-0"
                    >
                        <div className="rounded-[20px] border border-gray-300 bg-white p-2 sm:p-3">
                            <div className="overflow-hidden rounded-xl bg-gray-100">
                                <div className="flex h-10 items-center gap-2 border-b border-gray-200 bg-white px-4">
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary-300" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                                    <span className="ml-3 h-6 flex-1 rounded-md bg-gray-100" />
                                </div>
                                <div className="relative aspect-[1.25/1] overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1100&q=85"
                                        alt={t('heroImageAlt')}
                                        width="1100"
                                        height="880"
                                        className="h-full w-full object-cover"
                                        onError={handleImageError}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-ink/70 via-ink/10 to-transparent" />
                                    <div className="absolute bottom-6 left-6 max-w-xs text-white sm:bottom-8 sm:left-8">
                                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-200">DevenflyWeb / 2025</p>
                                        <p className="mt-2 font-display text-2xl font-bold leading-tight sm:text-3xl">{t('heroImageLabel')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
